import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheck } from "react-icons/fi";
import CollapsibleSection from "../CollapsibleSection";
import SectionSearch from "./SectionSearch";
import { filterOptions } from "./filterOptionUtils";
import styles from "../ViewOptionsPanel.module.scss";

const sameId = (a, b) => String(a ?? "") === String(b ?? "");

const DEFAULT_EMPTY_LIMIT = 8;
const MAX_RESULTS = 50;
const MAX_RECENT = 5;

const loadRecent = (key) => {
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

const saveRecent = (key, id) => {
  if (!key || id == null || id === "") return;
  try {
    const prev = loadRecent(key);
    const next = [String(id), ...prev.filter((x) => x !== String(id))].slice(
      0,
      MAX_RECENT,
    );
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* ignore */
  }
};

const PickerSection = ({
  title,
  mode = "single",
  value,
  selectedIds = [],
  onChange,
  options = [],
  allValue = "",
  allLabel = "Tümü",
  showAllOption = true,
  searchPlaceholder = "Ara...",
  emptyHint = "Aramak için yazın",
  emptyResultsLimit = DEFAULT_EMPTY_LIMIT,
  recentStorageKey = null,
  defaultOpen = true,
  loadOptions = null,
}) => {
  const [query, setQuery] = useState("");
  const [asyncOptions, setAsyncOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recentIds, setRecentIds] = useState(() =>
    loadRecent(recentStorageKey),
  );

  const isMulti = mode === "multi";
  const isLongList = options.length > emptyResultsLimit;
  const sourceOptions = asyncOptions ?? options;

  useEffect(() => {
    if (!loadOptions) {
      setAsyncOptions(null);
      return undefined;
    }

    const q = query.trim();
    if (q.length < 2) {
      setAsyncOptions(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await loadOptions(q);
        if (!cancelled) setAsyncOptions(Array.isArray(result) ? result : []);
      } catch {
        if (!cancelled) setAsyncOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, loadOptions]);

  const summaryLabel = useMemo(() => {
    if (isMulti) {
      if (!selectedIds.length) return allLabel;
      if (selectedIds.length === 1) {
        const opt = options.find((o) => sameId(o.value, selectedIds[0]));
        return opt?.label || `${selectedIds.length} seçili`;
      }
      return `${selectedIds.length} seçili`;
    }
    if (value == null || value === "" || sameId(value, allValue)) {
      return allLabel;
    }
    const opt = options.find((o) => sameId(o.value, value));
    return opt?.label || String(value);
  }, [
    isMulti,
    selectedIds,
    value,
    allValue,
    allLabel,
    options,
  ]);

  const visibleOptions = useMemo(() => {
    const q = query.trim();
    const usingAsync = !!loadOptions && q.length >= 2;

    if (usingAsync) {
      return (asyncOptions ?? []).slice(0, MAX_RESULTS);
    }

    if (!q && isLongList && !loadOptions) {
      const recentOptions = recentIds
        .map((id) => options.find((o) => sameId(o.value, id)))
        .filter(Boolean);
      return recentOptions.slice(0, MAX_RECENT);
    }

    return filterOptions(sourceOptions, query).slice(0, MAX_RESULTS);
  }, [
    query,
    loadOptions,
    asyncOptions,
    isLongList,
    recentIds,
    options,
    sourceOptions,
  ]);

  const showEmptyHint =
    isLongList &&
    !query.trim() &&
    !loadOptions &&
    visibleOptions.length === 0;

  const showAsyncHint =
    !!loadOptions && query.trim().length > 0 && query.trim().length < 2;

  const handleSelectSingle = useCallback(
    (nextValue) => {
      onChange?.(nextValue === allValue ? allValue : nextValue);
      if (nextValue != null && nextValue !== "" && nextValue !== allValue) {
        saveRecent(recentStorageKey, nextValue);
        setRecentIds(loadRecent(recentStorageKey));
      }
      setQuery("");
    },
    [onChange, allValue, recentStorageKey],
  );

  const toggleMulti = useCallback(
    (id) => {
      if (!onChange) return;
      if (id === null) {
        onChange([]);
        return;
      }
      const has = selectedIds.some((x) => sameId(x, id));
      onChange(
        has
          ? selectedIds.filter((x) => !sameId(x, id))
          : [...selectedIds, String(id)],
      );
      saveRecent(recentStorageKey, id);
      setRecentIds(loadRecent(recentStorageKey));
    },
    [onChange, selectedIds, recentStorageKey],
  );

  const renderSingleItem = (opt) => {
    const selected = sameId(value, opt.value);
    return (
      <button
        key={String(opt.value)}
        type="button"
        role="radio"
        aria-checked={selected}
        className={styles.pickerItem}
        onClick={() => handleSelectSingle(opt.value)}
      >
        <span
          className={`${styles.radioCircle} ${selected ? styles.radioCircleActive : ""}`}
        >
          {selected && <FiCheck size={9} />}
        </span>
        <span className={styles.pickerItemLabel}>{opt.label}</span>
      </button>
    );
  };

  const renderMultiItem = (opt) => {
    const checked = selectedIds.some((id) => sameId(id, opt.value));
    return (
      <button
        key={String(opt.value)}
        type="button"
        role="checkbox"
        aria-checked={checked}
        className={styles.pickerItem}
        onClick={() => toggleMulti(opt.value)}
      >
        <span
          className={`${styles.checkBox} ${checked ? styles.checkBoxActive : ""}`}
        >
          {checked && <FiCheck size={10} />}
        </span>
        <span className={styles.pickerItemLabel}>{opt.label}</span>
      </button>
    );
  };

  return (
    <CollapsibleSection title={title} defaultOpen={defaultOpen}>
      <div className={styles.picker}>
        <div className={styles.pickerSummary} title={summaryLabel}>
          {summaryLabel}
        </div>

        <SectionSearch
          value={query}
          onChange={setQuery}
          placeholder={searchPlaceholder}
          ariaLabel={`${title} ara`}
        />

        <div className={styles.pickerList} role="listbox" aria-label={title}>
          {isMulti && showAllOption && !query.trim() && (
            <button
              type="button"
              role="checkbox"
              aria-checked={selectedIds.length === 0}
              className={styles.pickerItem}
              onClick={() => toggleMulti(null)}
            >
              <span
                className={`${styles.checkBox} ${selectedIds.length === 0 ? styles.checkBoxActive : ""}`}
              >
                {selectedIds.length === 0 && <FiCheck size={10} />}
              </span>
              <span className={styles.pickerItemLabel}>{allLabel}</span>
            </button>
          )}

          {!isMulti && showAllOption && !query.trim() && (
            <button
              type="button"
              role="radio"
              aria-checked={value == null || value === "" || sameId(value, allValue)}
              className={styles.pickerItem}
              onClick={() => handleSelectSingle(allValue)}
            >
              <span
                className={`${styles.radioCircle} ${value == null || value === "" || sameId(value, allValue) ? styles.radioCircleActive : ""}`}
              >
                {(value == null || value === "" || sameId(value, allValue)) && (
                  <FiCheck size={9} />
                )}
              </span>
              <span className={styles.pickerItemLabel}>{allLabel}</span>
            </button>
          )}

          {loading && (
            <div className={styles.pickerHint}>Yükleniyor…</div>
          )}

          {showAsyncHint && (
            <div className={styles.pickerHint}>En az 2 karakter yazın</div>
          )}

          {showEmptyHint && (
            <div className={styles.pickerHint}>{emptyHint}</div>
          )}

          {!loading &&
            visibleOptions.map((opt) =>
              isMulti ? renderMultiItem(opt) : renderSingleItem(opt),
            )}

          {!loading &&
            query.trim() &&
            visibleOptions.length === 0 &&
            !showAsyncHint && (
              <div className={styles.pickerHint}>Sonuç bulunamadı</div>
            )}
        </div>
      </div>
    </CollapsibleSection>
  );
};

export default PickerSection;
